import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { RotateCcw, Search } from 'lucide-react'
import { listSales, scanDrafts } from '../../api/sales'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { FilterChips } from '../../components/PageParts'
import { fmtDateTime } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import SalesTabs from './SalesTabs'
import { PERIODS, money, num, periodRange, resumeRefundPath, saleNo, statusOf } from './salesShared'

// Refunds start from the original sale: find it here, then choose what is
// coming back on the next screen. Unfinished refunds wait at the top.
export default function RefundLookup() {
  const nav = useNavigate()
  const [period, setPeriod] = useState('week')
  const [[start, end], setRange] = useState(() => periodRange('week'))
  const [view, setView] = useState('sales')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [found, setFound] = useState(null)
  const [unfinished, setUnfinished] = useState([])
  const [loading, setLoading] = useState(true)
  const [errMsg, setErrMsg] = useState('')

  useEffect(() => {
    if (start && end && start > end) { setErrMsg('The start date must be on or before the end date.'); return }
    let cancelled = false
    setErrMsg('')
    setLoading(true)
    Promise.all([
      listSales({ start, end }).catch(() => []),
      scanDrafts({ start: periodRange('last-month')[0], end: periodRange('today')[1] }).catch(() => null),
    ]).then(([data, scan]) => {
      if (cancelled) return
      setRows(Array.isArray(data) ? data : [])
      setUnfinished(scan?.refund_rows || [])
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [start, end])

  // A sale number typed in full (SALE-0045 or 45) finds that sale even outside the period.
  useEffect(() => {
    const no = /^\s*(sale\s*-?\s*)?#?\d+\s*$/i.test(search) ? parseInt(search.replace(/\D/g, ''), 10) : 0
    if (!no || rows.some((r) => num(r.sale_no) === no && !r.original_sale_id)) { setFound(null); return undefined }
    let cancelled = false
    const timer = setTimeout(() => {
      listSales({ saleNo: no })
        .then((list) => { if (!cancelled) setFound((list || []).find((r) => !r.original_sale_id) || null) })
        .catch(() => { if (!cancelled) setFound(null) })
    }, 350)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [search, rows])

  const choosePeriod = (id) => {
    setPeriod(id)
    if (id !== 'custom') setRange(periodRange(id))
  }

  const sales = rows.filter((r) => statusOf(r) === 'posted')
  const refunds = rows.filter((r) => statusOf(r) === 'refunded')
  const shown = useMemo(() => {
    const list = view === 'sales' ? sales : refunds
    const term = search.trim().toLowerCase()
    const matches = list.filter((r) => !term || [saleNo(r), r.location_name, r.payment_method].join(' ').toLowerCase().includes(term))
    return found && view === 'sales' && statusOf(found) === 'posted' ? [found, ...matches] : matches
  }, [view, sales, refunds, search, found])

  return (
    <div className="ci-page">
      <SalesTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Refunds</h1>
            <div className="ci-title-meta">Find the sale the customer is returning, then choose the items coming back.</div>
          </div>
        </div>
      </div>

      {unfinished.length > 0 && (
        <section className="ci-table-wrap">
          <div className="list-card-head">
            <h2>Unfinished refunds</h2>
            <span>Finish or cancel these so stock and cash stay right.</span>
          </div>
          <table className="ci-table">
            <thead>
              <tr><th>Refund</th><th>Started</th><th>For sale</th><th className="num">Items</th><th /></tr>
            </thead>
            <tbody>
              {unfinished.map((row) => {
                const resume = resumeRefundPath(row)
                return (
                  <tr key={row.refund_sale_id || row.sale_id}>
                    <td>{saleNo(row)}</td>
                    <td className="ci-muted-cell">{fmtDateTime(row.sale_datetime)}</td>
                    <td>{row.original_sale_id ? <Link className="ci-je-link" to={`/sales/detail/${row.original_sale_id}`}>{docNo('sale', row.original_sale_no)}</Link> : '–'}</td>
                    <td className="num">{row.line_count ?? '–'}</td>
                    <td className="num">{resume && <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={resume}>Continue</Link>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search sales">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sales…" />
        </label>
        <select className="ci-select" value={period} onChange={(e) => choosePeriod(e.target.value)} aria-label="Period">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {period === 'custom' && (
          <span className="list-dates">
            <input type="date" className="ci-select" value={start} onChange={(e) => setRange([e.target.value, end])} aria-label="From" />
            <span>to</span>
            <input type="date" className="ci-select" value={end} onChange={(e) => setRange([start, e.target.value])} aria-label="To" />
          </span>
        )}
      </div>

      <FilterChips
        options={[
          { id: 'sales', label: 'Sales you can refund', count: sales.length },
          { id: 'refunds', label: 'Refunds given', count: refunds.length },
        ]}
        value={view}
        onChange={setView}
      />

      {errMsg && <ErrorBanner message={errMsg} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>{view === 'sales' ? 'Sale' : 'Refund'}</th>
                <th>Date and time</th>
                <th>Location</th>
                <th>{view === 'sales' ? 'Paid by' : 'Refunded by'}</th>
                <th className="num">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading sales…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{view === 'sales' ? 'No sales in this period. Try a longer period or type the sale number.' : 'No refunds in this period.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.sale_id}>
                  <td><Link className="ci-je-link" to={`/sales/detail/${r.sale_id}`}>{saleNo(r)}</Link></td>
                  <td className="ci-muted-cell">{fmtDateTime(r.sale_datetime)}</td>
                  <td>{r.location_name || '–'}</td>
                  <td>{r.payment_method || '–'}</td>
                  <td className="num ci-total">{money(Math.abs(num(r.total_amount ?? r.revenue)))}</td>
                  <td className="num">
                    {view === 'sales' && (
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => nav(`/sales/refund/${r.sale_id}`)}>
                        <RotateCcw size={13} />
                        Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
